import React, { createContext, useContext, useMemo, useCallback } from "react";
import type { FC, PropsWithChildren } from "react";
import { useTracking } from "@edreamsodigeo/frontend-core";

import { tracking, getTrackingFunctions } from "../config/tracking";
import type {
  TrackingContextProps,
  TrackOnContinueButtonClickProps,
  DualPriceContainerTrackingContextType,
} from "../contract";
import { DualPriceTrackingCategoriesEnum } from "../contract";

export const TrackingContext = createContext<TrackingContextProps>({
  trackOnContinueButtonClick: (_: TrackOnContinueButtonClickProps): void => {
    /* empty */
  },
  trackingCategory: DualPriceTrackingCategoriesEnum.EMPTY,
});

export const useTrackingContext = (): TrackingContextProps =>
  useContext(TrackingContext);

export const DualPriceTrackingProvider: FC<
  PropsWithChildren<DualPriceContainerTrackingContextType>
> = ({ itineraryData, trackingCategory, children }) => {
  const { trackEvent } = useTracking();

  const track = useCallback(
    (label: string): void => {
      trackEvent({
        category: trackingCategory,
        action: tracking.ACTION,
        label: label,
      });
    },
    [trackEvent, trackingCategory],
  );

  const trackingFunctions = useMemo(
    () => getTrackingFunctions({ track }),
    [track],
  );

  const value = useMemo(() => {
    const trackOnContinueButtonClick = ({
      wasSelectedPrimePrice,
    }: TrackOnContinueButtonClickProps): void => {
      trackingFunctions?.trackOnContinueButtonClick({
        itineraryData,
        wasSelectedPrimePrice,
      });
    };
    return {
      trackOnContinueButtonClick,
      trackingCategory,
    };
  }, [itineraryData, trackingFunctions, trackingCategory]);

  return (
    <TrackingContext.Provider value={value}>
      {children}
    </TrackingContext.Provider>
  );
};
